import { WuiIcon, IconName } from '@wui/common-library'
import {
  AnyConferenceChangedByOtherEvent,
  AnyConferenceChangedByYouEvent,
  CallPastMessage,
  HistoryMessageItem,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  ReactionAddedByOtherEvent,
  ReactionAddedByYouEvent,
  MessagePinnedByOtherEvent,
  isMessageDeleted,
  MessageCallEnded,
} from '@wui/im'
import { ComponentOptions, h, PropType, VNode, VNodeArrayChildren } from 'vue'

import { getCallEventText } from '@/entities/call-chat-event'
import { getSystemMessageText } from '@/entities/chat-system-message'
import { useStoreContacts } from '@/entities/contact'
import { draftModel, getPinnedEventText } from '@/entities/messages'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { bytesConverter } from '@/shared/utils/bytes-converter'
import { getFileTypeByName, IMAGE_TYPE } from '@/shared/utils/files'
import { linkRegex } from '@/shared/utils/link'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { getFullURL } from '@/shared/utils/url'

interface Props extends ComponentOptions {
  event?: HistoryMessageItem,
  draft?: draftModel.DraftMessage
}

const props = {
  event: {
    type: Object as PropType<HistoryMessageItem>,
    required: false,
  },
  draft: {
    type: Object as PropType<draftModel.DraftMessage>,
    required: false,
  },
}

export const NullPreview = (): VNode => h('span', useLocalization().t('mainAside.emptyMessages'))

const _DefaultPreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>

  const isDeleted = isMessageDeleted(typedEvent)

  const { t } = useLocalization()

  const messageText = typedEvent.item.messageText
  const previewText = isDeleted ? t('chat.messageRemoved') : messageText ?? ''

  let message: string | VNode = ''
  if (isDeleted) {
    message = h('i', previewText)
  } else {
    message = sanitizeToText(previewText)
  }

  const hasLink = !!messageText.match(linkRegex)?.length
  const isOutgoing = typedEvent.item.type === 'OutgoingMessageEvent'
  return h('span', { class: 'flex items-center' }, [
    h(WuiIcon, { name: (hasLink ? 'link' : isOutgoing ? 'messageBalloonOutlined' : 'messageBalloonAlt') as IconName, small: true, class: `text-fg-base-fg17 mr-1${isOutgoing ? ' -scale-x-100 ' : ' '}shrink-0` }),
    h('span', { class: 'truncate text-fg-base-fg17' }, message),
  ])
}

_DefaultPreview.props = props
export const DefaultPreview = _DefaultPreview

const _CallPreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<CallPastMessage>

  const { currentUser } = useCurrentUser()

  let iconName = 'arrowSouthWestM'
  if ((typedEvent.item as MessageCallEnded).srcPServed === currentUser.value?.imLogin) {
    iconName = 'arrowNorthEastM'
  }
  if (typedEvent.item.event === 'dialing_terminated') {
    iconName = 'callCanceled'
  }

  let colorClass = 'text-positive'
  if (typedEvent.item.event === 'call_missed' || typedEvent.item.event === 'dialing_terminated') {
    iconName = 'callMissed'
    colorClass = 'text-warning-shades-900'
  }

  return h('span', { class: 'flex items-center' }, [
    h(WuiIcon, { name: iconName as IconName, small: true, class: `${colorClass} mr-1 shrink-0` }),
    h('span', { class: 'truncate text-surf-brand-basedarkblue-def font-light' }, getCallEventText(typedEvent)),
  ])
}

_CallPreview.props = props
export const CallPreview = _CallPreview

const _DraftPreview = ({ draft }: Props): VNode | undefined => {
  const { t } = useLocalization()

  if (draft?.messageText) {
    const message = sanitizeToText(draft?.messageText ?? '')
    const cleanedString = message.replace(/&lt;/g, '<').replace(/&gt;/g, '>')

    return h('span', { class: 'flex items-center gap-1' }, [
      h('span', { class: 'text-fg-base-fg17 font-light' }, t('mainAside.draftWithMessage')),
      h('span', { class: 'truncate text-fg-base-fg17' }, cleanedString),
    ])
  }

  if (draft?.files && draft.files.length > 0) {
    const count = draft.files.length
    const firstFile = draft.files[0]?.file
    const isFirstFileIsImage = getFileTypeByName(firstFile?.name ?? '') === IMAGE_TYPE
    const key = isFirstFileIsImage ? t('mainAside.imageDraft', { size: bytesConverter(firstFile?.size), name: firstFile?.name }) : t('mainAside.fileDraft', { size: bytesConverter(firstFile?.size), name: firstFile?.name })
    const string = count > 1 ? t('mainAside.filesDraft', { count }) : key

    return h('span', { class: 'flex items-center gap-1', title: `${t('mainAside.draftWithMessage')} ${string}` }, [
      h('span', { class: 'text-fg-base-fg17 font-light' }, t('mainAside.draftWithMessage')),
      h('span', { class: 'truncate text-fg-base-fg17' }, string),
    ])
  }

  if (draft?.quotedEventId) {
    return h(
      'span',
      [
        h('span', { class: 'text-fg-base-fg17 font-light' }, t('mainAside.draftWithMessage')),
        h(WuiIcon, { name: 'formatQuote', class: 'w-4! h-4! text-fg-base-fg17!' }),
        h('span', { class: 'text-fg-base-fg17' }, t('common.quotedMessage')),
      ],
    )
  }
  return
}

_DraftPreview.props = props
export const DraftPreview = _DraftPreview

const _FilesPreview = ({ event }: Props): VNode => {
  const { t } = useLocalization()
  const typedEvent = event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  const messageText = sanitizeToText(typedEvent?.item?.messageText)
  const count = typedEvent?.item?.files?.length ?? 0
  const firstFile = typedEvent?.item?.files?.length ? typedEvent?.item?.files[0] : undefined
  const isFileIsTheOnlyAndIsImage = getFileTypeByName(firstFile?.filename ?? '') === IMAGE_TYPE && typedEvent.item.files?.length === 1
  const size = bytesConverter(firstFile?.filesize)
  const name = firstFile?.filename
  const imageWithMessageToRender: VNodeArrayChildren = [h('img', { src: getFullURL(`/files/${firstFile?.filecookie}`), class: 'h-4 w-4 rounded-2' }), h('span', { class: 'truncate' }, messageText)]
  const string = count > 1 ? t('mainAside.files', { count }) : `${name} ${size}`
  return isFileIsTheOnlyAndIsImage
    ? h('div', { class: 'flex gap-x-2 items-center' }, imageWithMessageToRender)
    : h('div', { class: 'flex items-center min-w-0' }, [
      h(WuiIcon, { name: 'attachFile' as IconName, small: true, class: 'text-fg-base-fg17 mr-1 shrink-0' }),
      h('span', { class: 'text-fg-base-fg17 font-light mr-1' }, string),
      h('span', { class: 'truncate' }, messageText),
    ])
}
_FilesPreview.props = props
export const FilesPreview = _FilesPreview

const _ReactionPreview = ({ event }: Props): VNode => {
  const reactionEvent = event as unknown as HistoryMessageItem<ReactionAddedByOtherEvent | ReactionAddedByYouEvent>

  const { t } = useLocalization()

  if ('rank' in reactionEvent.item) { // Added by other
    const { findByPServed } = useStoreContacts()
    const author = findByPServed(reactionEvent.item.executor)?.name
    return h('span', t('mainAside.otherReactionPreview', {
      author,
      reaction: reactionEvent.item.reactionType,
    }))
  } else {
    return h('span', t('mainAside.myReactionPreview', { reaction: reactionEvent.item.reactionType }))
  }
}
_ReactionPreview.props = props
export const ReactionPreview = _ReactionPreview

const _ForwardMessagePreview = (): VNode => {
  const { t } = useLocalization()

  const child: VNodeArrayChildren = []
  child.push(h(WuiIcon, {
    name: 'forward',
    small: true,
    class: 'mr-1 text-fg-base-fg17 shrink-0',
  }))
  child.push(h('span', { class: 'text-fg-base-fg17' }, t('chat.forwardMessageIsReady')))
  return h('div', child)
}
_ForwardMessagePreview.props = props
export const ForwardMessagePreview = _ForwardMessagePreview

export const ConferenceEditPreview = ({ event }: Props): VNode => {
  const typedMessage = event as HistoryMessageItem<AnyConferenceChangedByOtherEvent | AnyConferenceChangedByYouEvent>

  const { item: { type }} = typedMessage
  const children: VNodeArrayChildren = []
  if (type === 'ConferenceMemberAddedEvent') {
    children.push(h(WuiIcon, {
      name: 'addCircleOutlined',
      small: true,
      class: 'mr-1 text-surf-brand-basedarkblue-def shrink-0',
    }))
  }
  if (type === 'ConferenceMemberRemoved') {
    children.push(h(WuiIcon, {
      name: 'remove',
      small: true,
      class: 'mr-1 text-surf-brand-basedarkblue-def shrink-0',
    }))
  }
  if (type === 'ConferenceScheduleChanged' || type === 'ConferenceScheduleUpdated') {
    children.push(h(WuiIcon, {
      name: 'eventCalendar',
      small: true,
      class: 'mr-1 text-surf-brand-basedarkblue-def shrink-0',
    }))
  }

  children.push(h('span', { class: 'truncate text-surf-brand-basedarkblue-def font-light first-letter:capitalize' }, getSystemMessageText(typedMessage)))

  return h('span', { class: 'flex items-center' }, children)
}

export const PinMessagePreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<MessagePinnedByOtherEvent>
  return h('span', { class: 'flex items-center' }, [
    h(WuiIcon, { name: typedEvent.item.type === 'PinnedMessageEvent' ? 'pinnedM' : 'pinOffM', small: true, class: 'text-surf-brand-basedarkblue-def mr-1 shrink-0' }),
    h('span', { class: 'truncate text-surf-brand-basedarkblue-def font-light first-letter:capitalize' }, getPinnedEventText(typedEvent)),
  ])
}
